// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Menu  (route /menu)
// "Delivering to …" strip at the top of the menu. Shows the signed-in customer's DEFAULT saved address
// (GET /api/me/addresses) with the typical delivery window of the service zone it falls in
// (data/operations.ts) — nothing is invented: a guest, or a customer with no address, sees a prompt to set
// one. Links to the address book (signed in) or the login page (guest).
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, MapPin } from "lucide-react";
import useAuth from "../../hooks/useAuth";
import { fetchAddresses, type Address } from "../../utils/addresses";
import { findServiceZone } from "../../utils/geo";

const LABELS = { home: "Home", work: "Work", other: "Friends & family" } as const;

export default function DeliveryBar() {
  const auth = useAuth();
  const signedIn = auth.status === "signed-in";
  const [address, setAddress] = useState<Address | null>(null);

  useEffect(() => {
    if (!signedIn) return;
    let cancelled = false;
    fetchAddresses().then(
      (saved) => !cancelled && setAddress(saved[0] ?? null), // the API lists the default first
      () => undefined, // a failed lookup just shows the "set your location" prompt
    );
    return () => {
      cancelled = true;
    };
  }, [signedIn]);

  const zone = address ? findServiceZone(address.lat, address.lng) : null;
  const window = zone?.availability === "available" ? zone.typicalDeliveryWindow : undefined;

  return (
    <Link
      to={signedIn ? "/account/addresses" : "/welcome"}
      className="flex items-center gap-3 rounded-2xl bg-white p-3 shadow-sm ring-1 ring-ocean-900/5 transition-colors hover:bg-foam-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 dark:bg-ocean-900 dark:ring-white/10"
    >
      <span aria-hidden="true" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-teal-100 text-teal-700">
        <MapPin className="h-5 w-5" />
      </span>
      <span className="min-w-0 flex-1">
        {address ? (
          <>
            <span className="block text-sm font-bold text-ocean-950 dark:text-foam-50">Delivering to {LABELS[address.label]}</span>
            <span className="block truncate text-xs text-ocean-800/80 dark:text-foam-100/70">
              {address.house}, {address.street}, {address.city}
            </span>
          </>
        ) : (
          <>
            <span className="block text-sm font-bold text-ocean-950 dark:text-foam-50">Set your delivery location</span>
            <span className="block text-xs text-ocean-800/80 dark:text-foam-100/70">
              {signedIn ? "Add an address so we can check we deliver to you" : "Log in to save an address"}
            </span>
          </>
        )}
      </span>
      {window && (
        <span className="hidden shrink-0 rounded-full bg-teal-100 px-3 py-1 text-xs font-bold text-teal-700 sm:inline">{window}</span>
      )}
      <ChevronRight className="h-5 w-5 shrink-0 text-ocean-800/70 dark:text-foam-100/70" aria-hidden="true" />
    </Link>
  );
}
